import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../components/common/Button/Button';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import FormInput from '../../../components/common/FormInput/FormInput';
import FormSelect from '../../../components/common/FormSelect/FormSelect';
import ViewLinkButton from '../../../components/common/ViewLinkButton/ViewLinkButton';
import { CoachService, StudentService, WorkoutAssignmentService } from '../../../services';
import { WorkoutAssignmentRequestError } from '../../../services/WorkoutAssignmentService';
import type { CoachOption } from '../../../services/CoachService';
import type { StudentListItem } from '../../../types/student';
import type { TrainingItem } from '../../../types/training';
import {
  addBusinessDays,
  buildBrazilContactedAt,
  getBrazilCurrentCompetenceMonth,
  getDeliveredTrainingLabel,
  splitBrazilContactedAt,
} from '../../../utils';
import FupQuestionnaireFields from '../../Fup/FupQuestionnaireFields';
import {
  EMPTY_FUP_QUESTIONNAIRE,
  validateFupQuestionnaire,
  type FupQuestionnaireAnswers,
  type FupQuestionnaireErrors,
} from '../../Fup/fupQuestionnaire';
import performanceStyles from '../Performance.module.css';
import TrainingCatalogSearchModal from './TrainingCatalogSearchModal';
import styles from './LaunchWorkoutModal.module.css';

interface LaunchWorkoutModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  initialStudentId?: number;
  initialStudentName?: string;
  /** When true, expected delivery date is shown but not editable. */
  lockExpectedDeliveryDate?: boolean;
  /** Full edit of a delivered workout assignment. */
  historyEdit?: {
    historyId: number;
    assignmentId: number | null;
    competence: string;
    previousTrainingCode: string;
    expectedDeliveryDate: string;
    status: 'scheduled' | 'delivered';
    lastWorkoutDate: string;
    workoutCode: string;
    /** ISO timestamptz or date-only; split into date + time in the form. */
    deliveredAt: string;
    deliveredByPortalUserId: number | null;
  };
  /** Lightweight cycle edit when there is no workout history yet. */
  cycleEdit?: {
    assignmentId: number;
    previousTrainingCode: string;
    nextTrainingCode: string;
    expectedDeliveryDate: string;
    status?: 'scheduled' | 'delivered';
    /** ISO timestamptz or date-only. */
    deliveredAt?: string;
  };
}

function splitDeliveredAt(value: string | null | undefined): { date: string; time: string } {
  const parts = splitBrazilContactedAt(value);
  if (parts) {
    return parts;
  }

  const dateOnly = value?.trim().slice(0, 10) ?? '';
  return {
    date: /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : '',
    time: '',
  };
}

type DeliveryStatus = 'scheduled' | 'delivered';

/** Lançar/editar pelo modal: status sempre entregue. */
const STATUS_OPTIONS = [
  { value: 'delivered', label: 'Entregue' },
] as const;

const STUDENT_PAGE_SIZE = 30;
const STUDENT_SEARCH_DEBOUNCE_MS = 300;

const LaunchWorkoutModal: React.FC<LaunchWorkoutModalProps> = ({
  open,
  onClose,
  onSaved,
  initialStudentId,
  initialStudentName,
  lockExpectedDeliveryDate = false,
  historyEdit,
  cycleEdit,
}) => {
  const isHistoryEdit = Boolean(historyEdit);
  const isCycleEdit = Boolean(cycleEdit) && !isHistoryEdit;
  const isCreate = !isHistoryEdit && !isCycleEdit;
  const isExpectedDeliveryDateLocked = lockExpectedDeliveryDate && (isHistoryEdit || isCycleEdit);
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<Pick<StudentListItem, 'id' | 'full_name'> | null>(null);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [previousTrainingCode, setPreviousTrainingCode] = useState('');
  const [previousTrainingDate, setPreviousTrainingDate] = useState('');
  const [nextTrainingCode, setNextTrainingCode] = useState('');
  const [currentTrainingDate, setCurrentTrainingDate] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(() => addBusinessDays(new Date(), 4));
  const [deliveredDate, setDeliveredDate] = useState('');
  const [deliveredTime, setDeliveredTime] = useState('');
  const [lastWorkoutDate, setLastWorkoutDate] = useState('');
  const [deliveredByPortalUserId, setDeliveredByPortalUserId] = useState('');
  const [coachOptions, setCoachOptions] = useState<CoachOption[]>([]);
  const [status, setStatus] = useState<DeliveryStatus>('delivered');
  const [fupAnswers, setFupAnswers] = useState<FupQuestionnaireAnswers | null>(null);
  const [fupErrors, setFupErrors] = useState<FupQuestionnaireErrors>({});
  const [fupModalOpen, setFupModalOpen] = useState(false);
  const [isCatalogSearchOpen, setIsCatalogSearchOpen] = useState(false);
  const [catalogSearchTarget, setCatalogSearchTarget] = useState<'previous' | 'next'>('next');
  const [saving, setSaving] = useState(false);
  const [dateErrors, setDateErrors] = useState<{
    previousTrainingDate?: string;
    currentTrainingDate?: string;
    expectedDeliveryDate?: string;
    deliveredDate?: string;
    deliveredTime?: string;
  }>({});
  const studentSearchRequestIdRef = useRef(0);
  const studentSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastStudentSearchRef = useRef<string | null>(null);

  const loadStudents = useCallback(async (searchName: string) => {
    const requestId = studentSearchRequestIdRef.current + 1;
    studentSearchRequestIdRef.current = requestId;
    setLoadingStudents(true);

    try {
      const result = await StudentService.listPaginated(1, STUDENT_PAGE_SIZE, {
        searchName: searchName.trim() || undefined,
        filterIsInactive: 'false',
      });
      if (studentSearchRequestIdRef.current !== requestId) {
        return;
      }
      setStudents(result.data);
    } catch (error) {
      if (studentSearchRequestIdRef.current !== requestId) {
        return;
      }
      setStudents([]);
      alert(error instanceof Error ? error.message : 'Não foi possível carregar as alunas.');
    } finally {
      if (studentSearchRequestIdRef.current === requestId) {
        setLoadingStudents(false);
      }
    }
  }, []);

  const handleStudentSearchChange = useCallback((query: string) => {
    const normalizedQuery = query.trim();
    if (lastStudentSearchRef.current === normalizedQuery) {
      return;
    }

    clearTimeout(studentSearchDebounceRef.current);

    if (!normalizedQuery) {
      lastStudentSearchRef.current = normalizedQuery;
      void loadStudents('');
      return;
    }

    studentSearchDebounceRef.current = setTimeout(() => {
      lastStudentSearchRef.current = normalizedQuery;
      void loadStudents(normalizedQuery);
    }, STUDENT_SEARCH_DEBOUNCE_MS);
  }, [loadStudents]);

  useEffect(() => {
    if (!open) {
      clearTimeout(studentSearchDebounceRef.current);
      lastStudentSearchRef.current = null;
      studentSearchRequestIdRef.current += 1;
      return undefined;
    }

    return () => {
      clearTimeout(studentSearchDebounceRef.current);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setStudents([]);
    setSelectedStudentId(initialStudentId == null ? '' : String(initialStudentId));
    setSelectedStudent(initialStudentId == null
      ? null
      : { id: initialStudentId, full_name: initialStudentName?.trim() || 'Aluna atual' });
    setPreviousTrainingCode('');
    setPreviousTrainingDate('');
    setNextTrainingCode('');
    setCurrentTrainingDate('');
    setExpectedDeliveryDate(
      historyEdit?.expectedDeliveryDate
      ?? cycleEdit?.expectedDeliveryDate
      ?? addBusinessDays(new Date(), 4),
    );
    const deliveredParts = splitDeliveredAt(historyEdit?.deliveredAt ?? cycleEdit?.deliveredAt);
    setDeliveredDate(deliveredParts.date);
    setDeliveredTime(deliveredParts.time);
    setLastWorkoutDate(historyEdit?.lastWorkoutDate ?? '');
    setDeliveredByPortalUserId(historyEdit?.deliveredByPortalUserId ? String(historyEdit.deliveredByPortalUserId) : '');
    if (historyEdit) {
      setPreviousTrainingCode(historyEdit.previousTrainingCode);
      setNextTrainingCode(historyEdit.workoutCode);
    } else if (cycleEdit) {
      setPreviousTrainingCode(cycleEdit.previousTrainingCode);
      setNextTrainingCode(cycleEdit.nextTrainingCode);
    }
    setStatus('delivered');
    setFupAnswers(null);
    setFupErrors({});
    setDateErrors({});
    setFupModalOpen(false);
    setIsCatalogSearchOpen(false);
    setCatalogSearchTarget('next');
  }, [
    cycleEdit?.assignmentId,
    cycleEdit?.expectedDeliveryDate,
    cycleEdit?.nextTrainingCode,
    cycleEdit?.previousTrainingCode,
    cycleEdit?.status,
    cycleEdit?.deliveredAt,
    historyEdit?.historyId,
    historyEdit?.competence,
    historyEdit?.previousTrainingCode,
    historyEdit?.expectedDeliveryDate,
    historyEdit?.status,
    historyEdit?.lastWorkoutDate,
    historyEdit?.workoutCode,
    historyEdit?.deliveredAt,
    historyEdit?.deliveredByPortalUserId,
    initialStudentId,
    initialStudentName,
    open,
  ]);

  useEffect(() => {
    if (!open || !historyEdit) return undefined;
    let cancelled = false;
    void CoachService.listOptions()
      .then((options) => { if (!cancelled) setCoachOptions(options); })
      .catch((error) => {
        if (!cancelled) alert(error instanceof Error ? error.message : 'Não foi possível carregar as professoras.');
      });
    return () => { cancelled = true; };
  }, [historyEdit?.historyId, open]);

  const studentOptions = useMemo(() => {
    const options = students.map((student) => ({ value: String(student.id), label: student.full_name }));
    if (selectedStudentId && !options.some((option) => option.value === selectedStudentId) && selectedStudent) {
      options.unshift({ value: String(selectedStudent.id), label: selectedStudent.full_name });
    }
    return options;
  }, [selectedStudent, selectedStudentId, students]);

  const handleFupAnswerChange = useCallback(<K extends keyof FupQuestionnaireAnswers>(
    field: K,
    value: FupQuestionnaireAnswers[K],
  ) => {
    setFupAnswers((current) => ({ ...(current ?? EMPTY_FUP_QUESTIONNAIRE), [field]: value }));
    setFupErrors((current) => ({ ...current, [field]: undefined }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedStudentId) {
      alert('Selecione uma aluna.');
      return;
    }
    if (isCreate && !nextTrainingCode.trim()) {
      alert('Informe o treino atual.');
      return;
    }
    if (isCreate && !/^\d{4}-\d{2}-\d{2}$/.test(currentTrainingDate)) {
      alert('Informe a data do treino atual.');
      return;
    }
    if (isCreate && Boolean(previousTrainingCode.trim()) !== Boolean(previousTrainingDate)) {
      alert('Informe o treino anterior e a data do treino anterior juntos.');
      return;
    }
    if (isHistoryEdit && !nextTrainingCode.trim()) {
      alert(`Informe o ${getDeliveredTrainingLabel().toLowerCase()}.`);
      return;
    }
    if ((isHistoryEdit || isCycleEdit) && !/^\d{4}-\d{2}-\d{2}$/.test(expectedDeliveryDate)) {
      alert('Informe a data prevista de entrega.');
      return;
    }
    if (isHistoryEdit && !/^\d{4}-\d{2}-\d{2}$/.test(lastWorkoutDate)) {
      alert('Informe a data do treino.');
      return;
    }
    if (isHistoryEdit && (!/^\d+$/.test(deliveredByPortalUserId) || Number(deliveredByPortalUserId) <= 0)) {
      alert('Informe quem entregou o treino.');
      return;
    }

    const resolvedDeliveredDate = /^\d{4}-\d{2}-\d{2}$/.test(deliveredDate)
      ? deliveredDate
      : (isHistoryEdit
        ? lastWorkoutDate
        : (expectedDeliveryDate || currentTrainingDate));
    const resolvedDeliveredTime = /^\d{2}:\d{2}$/.test(deliveredTime) ? deliveredTime : '12:00';

    if ((isHistoryEdit || isCycleEdit) && !/^\d{4}-\d{2}-\d{2}$/.test(resolvedDeliveredDate)) {
      alert('Informe a data de entrega.');
      return;
    }

    const deliveredAt = buildBrazilContactedAt(resolvedDeliveredDate, resolvedDeliveredTime);
    if ((isHistoryEdit || isCycleEdit) && !deliveredAt) {
      alert('Informe a data e hora de entrega.');
      return;
    }

    if (isCreate && fupAnswers) {
      const errors = validateFupQuestionnaire(fupAnswers);
      setFupErrors(errors);
      if (Object.keys(errors).length > 0) {
        setFupModalOpen(true);
        return;
      }
    }

    setSaving(true);
    setDateErrors({});
    try {
      if (historyEdit) {
        const competenceMonth = lastWorkoutDate.slice(0, 7) || getBrazilCurrentCompetenceMonth();
        await WorkoutAssignmentService.updatePerformanceAssignment({
          assignmentId: historyEdit.assignmentId,
          historyId: historyEdit.historyId,
          studentId: Number(selectedStudentId),
          competence: `${competenceMonth}-01`,
          previousTrainingCode: previousTrainingCode.trim() || null,
          nextTrainingCode: nextTrainingCode.trim().toUpperCase(),
          expectedDeliveryDate,
          status: 'delivered',
          lastWorkoutDate,
          deliveredDate: resolvedDeliveredDate,
          deliveredAt: deliveredAt ?? undefined,
          deliveredByPortalUserId: Number(deliveredByPortalUserId) || 0,
        });
      } else if (cycleEdit) {
        await WorkoutAssignmentService.updatePerformanceCycle({
          assignmentId: cycleEdit.assignmentId,
          previousTrainingCode: previousTrainingCode.trim().toUpperCase() || null,
          nextTrainingCode: nextTrainingCode.trim().toUpperCase() || null,
          expectedDeliveryDate,
          deliveredAt: deliveredAt ?? undefined,
          deliveredDate: resolvedDeliveredDate,
        });
      } else {
        await WorkoutAssignmentService.createPerformanceAssignment({
          studentId: Number(selectedStudentId),
          previousTrainingCode: previousTrainingCode.trim() || null,
          previousTrainingDate: previousTrainingDate || null,
          currentTrainingCode: nextTrainingCode.trim().toUpperCase(),
          currentTrainingDate,
          ...(fupAnswers ? { fupAnswers } : {}),
        });
      }
      onSaved();
      onClose();
    } catch (error) {
      if (error instanceof WorkoutAssignmentRequestError && Object.keys(error.fields).length > 0) {
        setDateErrors({
          previousTrainingDate: error.fields.previous_training_date,
          currentTrainingDate: error.fields.current_training_date,
          expectedDeliveryDate: error.fields.expected_delivery_date,
          deliveredDate: error.fields.delivered_date,
        });
        return;
      }
      alert(error instanceof Error ? error.message : 'Não foi possível salvar o treino.');
    } finally {
      setSaving(false);
    }
  }, [
    currentTrainingDate,
    cycleEdit,
    deliveredByPortalUserId,
    deliveredDate,
    deliveredTime,
    expectedDeliveryDate,
    fupAnswers,
    historyEdit,
    isCreate,
    isCycleEdit,
    isHistoryEdit,
    lastWorkoutDate,
    nextTrainingCode,
    onClose,
    onSaved,
    previousTrainingCode,
    previousTrainingDate,
    selectedStudentId,
    status,
  ]);

  const handleConfirmFup = useCallback(() => {
    const answers = fupAnswers ?? EMPTY_FUP_QUESTIONNAIRE;
    const errors = validateFupQuestionnaire(answers);
    setFupErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setFupAnswers(answers);
    setFupModalOpen(false);
  }, [fupAnswers]);

  return (
    <>
      <ConfirmModal
        open={open}
        title={isHistoryEdit ? 'Editar histórico de treino' : isCycleEdit ? 'Editar treino' : 'Lançar treino'}
        message={
          isHistoryEdit
            ? 'Atualize os dados do treino registrado.'
            : isCycleEdit
              ? 'Atualize o treino anterior, o próximo treino e a data prevista de entrega.'
              : 'Informe a aluna e os dados do novo ciclo de treino.'
        }
        confirmLabel={isHistoryEdit || isCycleEdit ? 'Salvar alterações' : 'Lançar treino'}
        cancelLabel="Cancelar"
        variant="info"
        icon="fitness_center"
        width={640}
        alignment="left"
        loading={saving}
        onCancel={() => {
          if (isCatalogSearchOpen) {
            setIsCatalogSearchOpen(false);
            return;
          }
          onClose();
        }}
        onConfirm={handleSave}
      >
        <div className={performanceStyles['context-fields']}>
          <FormSelect
            id="launch-workout-student"
            label="Aluna"
            required
            value={selectedStudentId}
            onChange={(event) => {
              const value = event.target.value;
              setSelectedStudentId(value);
              setSelectedStudent(students.find((student) => String(student.id) === value) ?? null);
            }}
            options={studentOptions}
            placeholder="Selecione a aluna"
            searchPlaceholder="Pesquisar aluna"
            loading={loadingStudents}
            onSearchChange={isCreate ? handleStudentSearchChange : undefined}
            disabled={saving || initialStudentId != null}
          />

          <div className={styles['training-rows']}>
            <div
              className={[
                styles['training-pair-row'],
                (isHistoryEdit || isCycleEdit) ? styles['training-pair-row-single'] : '',
              ].filter(Boolean).join(' ')}
            >
              <div className={styles['training-field-row']}>
                <FormInput
                  id="launch-workout-previous-training"
                  label="Treino anterior"
                  value={previousTrainingCode}
                  onChange={(event) => setPreviousTrainingCode(event.target.value.toUpperCase())}
                  placeholder="Ex.: B07"
                  disabled={saving}
                  containerClassName={styles['training-input']}
                  className={performanceStyles['training-code-input']}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  className={styles['training-search-button']}
                  aria-label="Buscar treino anterior"
                  title="Buscar treino anterior"
                  disabled={saving}
                  icon="search"
                  onClick={() => {
                    setCatalogSearchTarget('previous');
                    setIsCatalogSearchOpen(true);
                  }}
                />
              </div>
              {isCreate && (
                <FormInput
                  id="launch-workout-previous-date"
                  label="Data do treino anterior"
                  type="date"
                  value={previousTrainingDate}
                  onChange={(event) => {
                    setPreviousTrainingDate(event.target.value);
                    setDateErrors((current) => ({ ...current, previousTrainingDate: undefined }));
                  }}
                  error={dateErrors.previousTrainingDate}
                  disabled={saving}
                />
              )}
            </div>

            <div
              className={[
                styles['training-pair-row'],
                (isHistoryEdit || isCycleEdit) ? styles['training-pair-row-single'] : '',
              ].filter(Boolean).join(' ')}
            >
              <div className={styles['training-field-row']}>
                <FormInput
                  id="launch-workout-next-training"
                  label={isHistoryEdit ? getDeliveredTrainingLabel() : isCycleEdit ? 'Próximo treino' : 'Treino atual'}
                  value={nextTrainingCode}
                  onChange={(event) => setNextTrainingCode(event.target.value.toUpperCase())}
                  placeholder="Ex.: B07"
                  required={isCreate}
                  disabled={saving}
                  containerClassName={styles['training-input']}
                  className={performanceStyles['training-code-input']}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  className={styles['training-search-button']}
                  aria-label="Buscar treino"
                  title="Buscar treino"
                  disabled={saving}
                  icon="search"
                  onClick={() => {
                    setCatalogSearchTarget('next');
                    setIsCatalogSearchOpen(true);
                  }}
                />
              </div>
              {isCreate && (
                <FormInput
                  id="launch-workout-current-date"
                  label="Data do treino atual"
                  type="date"
                  value={currentTrainingDate}
                  onChange={(event) => {
                    setCurrentTrainingDate(event.target.value);
                    setDateErrors((current) => ({ ...current, currentTrainingDate: undefined }));
                  }}
                  error={dateErrors.currentTrainingDate}
                  required
                  disabled={saving}
                />
              )}
            </div>
          </div>

          {isHistoryEdit && <FormInput
            id="launch-workout-history-date"
            label="Data do treino"
            type="date"
            value={lastWorkoutDate}
            onChange={(event) => setLastWorkoutDate(event.target.value)}
            required
            disabled={saving}
          />}

          {isCycleEdit && (
            <div className={styles['delivery-grid']}>
              <FormInput
                id="launch-workout-cycle-expected-date"
                label="Data prevista de entrega"
                type="date"
                value={expectedDeliveryDate}
                onChange={(event) => {
                  if (isExpectedDeliveryDateLocked) return;
                  setExpectedDeliveryDate(event.target.value);
                  setDateErrors((current) => ({ ...current, expectedDeliveryDate: undefined }));
                }}
                error={dateErrors.expectedDeliveryDate}
                required
                disabled={saving || isExpectedDeliveryDateLocked}
                readOnly={isExpectedDeliveryDateLocked}
              />
              <FormInput
                id="launch-workout-cycle-delivered-date"
                label="Data de entrega"
                type="date"
                value={deliveredDate}
                onChange={(event) => {
                  setDeliveredDate(event.target.value);
                  setDateErrors((current) => ({ ...current, deliveredDate: undefined }));
                }}
                error={dateErrors.deliveredDate}
                required
                disabled={saving}
              />
              <FormInput
                id="launch-workout-cycle-delivered-time"
                label="Hora de entrega"
                type="time"
                value={deliveredTime}
                onChange={(event) => {
                  setDeliveredTime(event.target.value);
                  setDateErrors((current) => ({ ...current, deliveredTime: undefined }));
                }}
                error={dateErrors.deliveredTime}
                required
                disabled={saving}
              />
            </div>
          )}

          {isCreate && <div className={styles['fup-section']}>
            <div>
              <strong>FUP</strong>
              <span>{fupAnswers ? 'Respostas preenchidas' : 'Opcional'}</span>
            </div>
            <div className={styles['fup-actions']}>
              {fupAnswers && (
                <button type="button" className={styles['remove-fup']} onClick={() => setFupAnswers(null)} disabled={saving}>
                  Remover
                </button>
              )}
              <ViewLinkButton
                label={fupAnswers ? 'Editar FUP' : 'Inserir FUP'}
                icon={null}
                onClick={() => setFupModalOpen(true)}
                disabled={saving}
              />
            </div>
          </div>}

          {isHistoryEdit && <div className={styles['delivery-grid']}>
            <FormInput
              id="launch-workout-delivery-date"
              label="Data prevista"
              type="date"
              value={expectedDeliveryDate}
              onChange={(event) => {
                if (isExpectedDeliveryDateLocked) return;
                setExpectedDeliveryDate(event.target.value);
                setDateErrors((current) => ({ ...current, expectedDeliveryDate: undefined }));
              }}
              error={dateErrors.expectedDeliveryDate}
              required
              disabled={saving || isExpectedDeliveryDateLocked}
              readOnly={isExpectedDeliveryDateLocked}
            />
            <FormSelect
              id="launch-workout-status"
              label="Status"
              required
              value="delivered"
              onChange={() => {
                setStatus('delivered');
              }}
              options={[...STATUS_OPTIONS]}
              allowEmpty={false}
              disabled
            />
            <FormInput
              id="launch-workout-delivered-date"
              label="Data entrega"
              type="date"
              value={deliveredDate}
              onChange={(event) => {
                setDeliveredDate(event.target.value);
                setDateErrors((current) => ({ ...current, deliveredDate: undefined }));
              }}
              error={dateErrors.deliveredDate}
              required
              disabled={saving}
            />
            <FormInput
              id="launch-workout-delivered-time"
              label="Hora entrega"
              type="time"
              value={deliveredTime}
              onChange={(event) => {
                setDeliveredTime(event.target.value);
                setDateErrors((current) => ({ ...current, deliveredTime: undefined }));
              }}
              error={dateErrors.deliveredTime}
              required
              disabled={saving}
            />
            <FormSelect
              id="launch-workout-delivered-by"
              label="Entregue por"
              required
              value={deliveredByPortalUserId}
              onChange={(event) => setDeliveredByPortalUserId(event.target.value)}
              options={coachOptions.map((coach) => ({ value: String(coach.portal_user_id), label: coach.label }))}
              placeholder="Selecione..."
              disabled={saving}
            />
          </div>}
        </div>
      </ConfirmModal>

      <TrainingCatalogSearchModal
        open={isCatalogSearchOpen}
        onCancel={() => setIsCatalogSearchOpen(false)}
        onSelect={(training: TrainingItem) => {
          const workoutCode = training.name.trim().toUpperCase();
          if (catalogSearchTarget === 'previous') {
            setPreviousTrainingCode(workoutCode);
          } else {
            setNextTrainingCode(workoutCode);
          }
          setIsCatalogSearchOpen(false);
        }}
      />

      {isCreate && <ConfirmModal
        open={fupModalOpen}
        title={`FUP${selectedStudent ? ` — ${selectedStudent.full_name}` : ''}`}
        message="Preencha as mesmas respostas do formulário público de FUP."
        confirmLabel="Usar estas respostas"
        cancelLabel="Voltar"
        variant="info"
        width={640}
        alignment="left"
        onCancel={() => setFupModalOpen(false)}
        onConfirm={handleConfirmFup}
      >
        <div className={styles['fup-fields']}>
          <FupQuestionnaireFields
            answers={fupAnswers ?? EMPTY_FUP_QUESTIONNAIRE}
            errors={fupErrors}
            showCopyButtons
            onChange={handleFupAnswerChange}
          />
        </div>
      </ConfirmModal>}
    </>
  );
};

export default LaunchWorkoutModal;
